import { Flex } from 'antd';
import { type ChangeEvent, type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './attachments-upload-input.module.scss';
import { Text } from '@shared/ui/antd';

import { useAttachmentsContext } from '../context/attachments-context';
import { allowedFileTypes } from '../utils/allowed-file-types';
import { formatFileSize } from '../utils/format-file-size';
import { ErrorCode, validateFiles } from '../utils/validate-files';
import classNames from 'classnames';
import { useNotify } from '@shared/hooks/use-notify';

interface AttachmentsUploadInputProps {
  isDisabled?: boolean;
  isMulti?: boolean;
  accept?: string[];
  maxSize?: number;
  maxFiles?: number;
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  className?: string;
  variant?: 'default' | 'knowledgeBase';
}

export const AttachmentsUploadInput: FC<AttachmentsUploadInputProps> = ({
  isDisabled = false,
  isMulti = true,
  accept = allowedFileTypes,
  maxSize = 5e6,
  maxFiles = 10,
  title,
  subtitle,
  icon,
  className,
  variant = 'default',
}) => {
  const { t } = useTranslation('translation');
  const notify = useNotify();
  const { fileKeys, setFileToUpload } = useAttachmentsContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const maxFilesLeft = maxFiles - fileKeys.length;
  const isAllowedToUpload = maxFilesLeft > 0;

  const handleButtonClick = () => inputRef.current?.click();

  const openNotification = (message: string) => {
    notify({ message });
  };

  const validateAndUploadFiles = (files: FileList | File[]) => {
    const restrictions = { maxFiles: maxFilesLeft, maxSize, accept };
    const { acceptedFiles, errorCodes } = validateFiles(Array.from(files), restrictions);
    acceptedFiles.forEach((file) => setFileToUpload(file));
    errorCodes.forEach((code) => {
      let errorMessage = '';
      switch (code) {
        case ErrorCode.TooManyFiles:
          errorMessage = t('common.fileValidationErrors.tooManyFiles', { maxFiles });
          break;
        case ErrorCode.FileTooLarge:
          errorMessage = t('common.fileValidationErrors.fileTooLarge');
          errorMessage += formatFileSize(maxSize, 0);
          break;
        case ErrorCode.FileInvalidType:
          errorMessage = t('common.fileValidationErrors.fileInvalidType');
          break;
        default:
          errorMessage = t('common.commonErrors.unknownError');
      }
      openNotification(errorMessage);
    });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files?.length) {
      validateAndUploadFiles(files);

      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    if (e.dataTransfer.files?.length) {
      validateAndUploadFiles(e.dataTransfer.files);
    }
  };

  useEffect(() => {
    const resetDrag = () => {
      dragCounter.current = 0;
      setIsDragging(false);
    };
    window.addEventListener('dragend', resetDrag);
    window.addEventListener('drop', resetDrag);
    return () => {
      window.removeEventListener('dragend', resetDrag);
      window.removeEventListener('drop', resetDrag);
    };
  }, []);

  const resolvedTitle = isDragging
    ? t('common.attachments.dropFilesHere')
    : title === undefined
      ? t('common.attachments.placeholderTitle')
      : title;
  const resolvedSubtitle =
    subtitle === undefined ? t('common.attachments.placeholderSubtitle') : subtitle;

  return (
    <>
      <input
        ref={inputRef}
        tabIndex={-1}
        type="file"
        multiple={isMulti}
        accept={accept?.length ? accept.join(', ') : undefined}
        onChange={handleInputChange}
        disabled={isDisabled || !isAllowedToUpload}
        style={{ display: 'none' }}
      />

      <Flex
        justify="center"
        align="center"
        gap="12px"
        className={classNames(
          styles.root,
          {
            [styles.knowledgeBase]: variant === 'knowledgeBase',
          },
          className,
          'noselect',
          {
            [styles.dragging]: isDragging,
          },
        )}
        onClick={isDisabled || !isAllowedToUpload ? () => {} : handleButtonClick}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        vertical
      >
        {icon && <span className={styles.icon}>{icon}</span>}

        {resolvedTitle && (
          <Text variant="md" fontWeight={600} className={classNames(styles.title, 'noselect')}>
            {resolvedTitle}
          </Text>
        )}

        {!isDragging && resolvedSubtitle && (
          <Text variant="sm" className={classNames(styles.subtitle, 'noselect')}>
            {resolvedSubtitle}
          </Text>
        )}
      </Flex>
    </>
  );
};
